import React from 'react';
import { cn } from '@/lib/utils';

/**
 * PageHeader - Standardized page header component
 * 
 * @param {string} title - Main page title (H1)
 * @param {string} subtitle - Optional subtitle/description
 * @param {React.ReactNode} actions - Primary action buttons (right side)
 * @param {React.ReactNode} children - Additional content below header
 * @param {string} className - Additional classes
 */
export function PageHeader({ 
  title, 
  subtitle, 
  actions, 
  children,
  className,
  ...props 
}) {
  return (
    <div 
      className={cn("mb-6", className)} 
      data-testid="page-header"
      {...props}
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h1 
            className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900"
            data-testid="page-title"
          >
            {title}
          </h1>
          {subtitle && (
            <p 
              className="text-sm text-slate-500 mt-1"
              data-testid="page-subtitle"
            >
              {subtitle}
            </p>
          )}
        </div>
        {actions && (
          <div className="flex items-center gap-2 flex-shrink-0">
            {actions}
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

/**
 * PageHeaderCompact - Mobile-optimized sticky header
 */
export function PageHeaderCompact({ 
  title, 
  subtitle,
  leftAction,
  rightAction,
  className,
  ...props 
}) {
  return (
    <div 
      className={cn(
        "sticky top-0 z-20 bg-white border-b border-slate-200 px-4 py-3",
        className
      )}
      data-testid="page-header-compact"
      {...props}
    >
      <div className="flex items-center justify-between gap-3">
        {leftAction && (
          <div className="flex-shrink-0">
            {leftAction}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-bold text-slate-900 truncate">
            {title}
          </h1>
          {subtitle && (
            <p className="text-xs text-slate-500 truncate">
              {subtitle}
            </p>
          )}
        </div>
        {rightAction && (
          <div className="flex-shrink-0">
            {rightAction}
          </div>
        )}
      </div>
    </div>
  );
}

export default PageHeader;
